import React from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { submitImprovementSuggestion } from '../../api';
import { logError, logInfo } from '../../logging/loggingService';

const CATEGORIES = [
    { value: '', label: 'Keine Angabe' },
    { value: 'Benutzeroberflaeche', label: 'Benutzeroberfläche' },
    { value: 'Funktionalitaet', label: 'Funktionalität' },
    { value: 'Performance', label: 'Performance' },
    { value: 'Sonstiges', label: 'Sonstiges' },
];

export default function SuggestionReportDialog({ open, onClose, reporter }) {
    const location = useLocation();
    const [title, setTitle] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [category, setCategory] = React.useState('');
    const [feedback, setFeedback] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        setTitle('');
        setDescription('');
        setCategory('');
        setFeedback(null);
        setSubmitting(false);
    }, [open]);

    const handleClose = () => {
        if (submitting) {
            return;
        }
        onClose?.();
    };

    const hasRequired = title.trim() && description.trim();

    const handleSubmit = async () => {
        if (!hasRequired || submitting) {
            return;
        }
        setSubmitting(true);
        setFeedback(null);

        const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const locale = typeof navigator !== 'undefined' ? navigator.language : '';
        const referrer = typeof document !== 'undefined' ? document.referrer : '';

        const payload = {
            title: title.trim(),
            description: description.trim(),
            category: category.trim() || undefined,
            pageUrl,
            userAgent,
            locale,
            appVersion: process.env.REACT_APP_VERSION || undefined,
            metadata: {
                path: location?.pathname,
                search: location?.search,
                hash: location?.hash,
                referrer: referrer || undefined,
                userId: reporter?.id || undefined,
            },
        };

        try {
            const result = await submitImprovementSuggestion(payload);
            const created = Boolean(result?.data?.created);
            const message = result?.data?.message
                || (created
                    ? 'Vielen Dank fuer deinen Verbesserungsvorschlag!'
                    : 'Vorschlag wurde verarbeitet.');
            setFeedback({ severity: 'success', message });
            setTitle('');
            setDescription('');
            setCategory('');
            logInfo('Suggestion submitted', { component: 'SuggestionReportDialog', created });
        } catch (error) {
            logError('Suggestion submit failed', error, { component: 'SuggestionReportDialog' });
            setFeedback({
                severity: 'error',
                message: 'Vorschlag konnte nicht gesendet werden. Bitte spaeter erneut versuchen.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Verbesserungsvorschlag einreichen</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {feedback && (
                        <Alert severity={feedback.severity}>
                            <Stack spacing={0.5}>
                                <Typography variant="body2">{feedback.message}</Typography>
                            </Stack>
                        </Alert>
                    )}
                    <TextField
                        label="Titel"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        required
                        fullWidth
                        autoFocus
                    />
                    <TextField
                        label="Beschreibung"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        required
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Beschreibe deine Idee oder den Verbesserungsvorschlag moeglichst konkret."
                    />
                    <TextField
                        label="Kategorie (optional)"
                        value={category}
                        onChange={(event) => setCategory(event.target.value)}
                        fullWidth
                        select
                    >
                        {CATEGORIES.map((option) => (
                            <MenuItem key={option.value || 'none'} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Typography variant="caption" color="text.secondary">
                        Seite: {location?.pathname || '-'}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    Abbrechen
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!hasRequired || submitting}
                >
                    {submitting ? 'Absenden...' : 'Absenden'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
